import * as Sentry from '@sentry/react';
import { getEnv } from 'utils/utils';

export default class MessageController {
  constructor(htmlWindow, origin, options) {
    if (typeof htmlWindow?.postMessage !== 'function')
      throw new TypeError('htmlWindow must have a postMessage method available');

    if (getEnv() === 'production' && origin === '*') {
      throw new Error('Origin cannot be *');
    }

    this.origin = origin;
    this.win = htmlWindow;
    this.debug = options?.debug || false;
    this.sendLogger =
      options?.sendLogger ||
      function (type, payload) {
        console.log(`%c[Sent] ${type}:`, 'color: lightgreen;', payload);
      };
    this.receiveLogger =
      options?.receiveLogger ||
      function (type, payload) {
        console.log(`%c[Received] ${type}:`, 'color: orange;', payload);
      };
  }

  _logBreadcrumb(type, payload, category) {
    // this matches the Sentry breadcrumb pattern used by the Redux Sentry enhancer
    Sentry.addBreadcrumb({
      type: 'info',
      category: `messenger.${category}`,
      data: { type, payload },
    });
  }

  _send(type, payload) {
    const message = { type };
    if (payload !== undefined) message.payload = payload;
    this.win.postMessage(message, this.origin);
    if (this.debug) this.sendLogger(type, payload);

    this._logBreadcrumb(type, payload, 'sent');
  }

  _isTypeMatch(typeMatcher, type) {
    if (typeof typeMatcher === 'function') return typeMatcher(type);
    return typeMatcher === type;
  }

  _off(cb) {
    window.removeEventListener('message', cb);
  }

  _on(typeMatcher, cb) {
    const eventHandler = (event) => {
      if (!this.isOriginMatch(event.origin)) return;

      const { data } = event;
      if (!data.type) return;

      if (!this._isTypeMatch(typeMatcher, data.type)) return;
      if (this.debug) this.receiveLogger(data.type, data.payload);
      this._logBreadcrumb(data.type, data.payload, 'received');
      cb(event.data);
    };

    window.addEventListener('message', eventHandler);

    return () => this._off(eventHandler);
  }

  isOriginMatch(receivedOrigin) {
    if (receivedOrigin === window.origin) return false; // don't on the same origin as this app

    if (this.origin === '*') return true;
    return this.origin === receivedOrigin;
  }
}
